import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Loader2, X, UserPlus } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useAuth } from '@/hooks/useAuth';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface TeamMember {
  user_id: string;
  first_name: string;
  last_name: string;
  email: string;
}

interface SharedUser {
  id: string;
  shared_with: string;
  profiles: {
    first_name: string;
    last_name: string;
    email: string;
  };
}

interface AreaSharingDialogProps {
  isOpen: boolean;
  onClose: () => void;
  areaId: string;
  areaName: string;
  onSharesUpdated?: () => void;
}

// Helper function to mask email for privacy (heavy masking for dropdowns)
const maskEmail = (email: string): string => {
  const [localPart, domain] = email.split('@');
  if (!domain) return '****';
  
  const maskedLocal = localPart.length > 2 
    ? localPart.substring(0, 2) + '****' 
    : '****';
  
  const maskedDomain = domain.length > 1
    ? domain.substring(0, 1) + '****'
    : '****';
  
  return `${maskedLocal}@${maskedDomain}`;
};

// Lighter email masking for owner info (shows more)
const lightMaskEmail = (email: string): string => {
  const [localPart, domain] = email.split('@');
  if (!domain) return '****';
  
  // Mask local part
  let maskedLocal;
  if (localPart.length <= 4) {
    maskedLocal = `${localPart.substring(0, 1)}***`;
  } else {
    const firstPart = localPart.substring(0, 3);
    const lastPart = localPart.substring(localPart.length - 1);
    maskedLocal = `${firstPart}***${lastPart}`;
  }
  
  // Mask domain
  const [domainName, tld] = domain.split('.');
  const maskedDomain = domainName.length > 2
    ? `${domainName.substring(0, 2)}***`
    : '***';
  
  return `${maskedLocal}@${maskedDomain}.${tld || '***'}`;
};

export const AreaSharingDialog = ({ isOpen, onClose, areaId, areaName, onSharesUpdated }: AreaSharingDialogProps) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [sharedUsers, setSharedUsers] = useState<SharedUser[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [isSharing, setIsSharing] = useState(false);
  const [isOwner, setIsOwner] = useState(false);
  const [areaOwner, setAreaOwner] = useState<{ first_name: string; last_name: string; email: string } | null>(null);
  const [sharedByUser, setSharedByUser] = useState<{ first_name: string; last_name: string; email: string } | null>(null);

  useEffect(() => {
    if (isOpen && user) {
      loadData();
    }
  }, [isOpen, user, areaId]);

  const loadData = async () => {
    if (!user) return;

    setLoading(true);
    try {
      // Check if user is the owner of this area
      const { data: areaData } = await supabase
        .from('d2d_areas')
        .select('created_by')
        .eq('id', areaId)
        .single();

      const userIsOwner = areaData?.created_by === user.id;
      setIsOwner(userIsOwner);

      // Get owner profile info
      if (areaData?.created_by) {
        const { data: ownerProfile } = await supabase
          .from('profiles')
          .select('first_name, last_name, email')
          .eq('user_id', areaData.created_by)
          .single();

        setAreaOwner(ownerProfile);
      }

      // If not owner, check who shared it with this user
      if (!userIsOwner) {
        const { data: shareData } = await supabase
          .from('d2d_area_shares')
          .select('shared_by')
          .eq('area_id', areaId)
          .eq('shared_with', user.id)
          .single();

        if (shareData && shareData.shared_by !== areaData?.created_by) {
          // Only set if different from owner
          const { data: sharedProfile } = await supabase
            .from('profiles')
            .select('first_name, last_name, email')
            .eq('user_id', shareData.shared_by)
            .single();

          setSharedByUser(sharedProfile);
        }
        setLoading(false);
        return;
      }

      // Get user's team (only for owners)
      const { data: profile } = await supabase
        .from('profiles')
        .select('team_id, organization_id')
        .eq('user_id', user.id)
        .single();

      console.log('Current user profile:', profile);

      if (!profile?.team_id) {
        toast.error('Sie sind keinem Team zugeordnet');
        setLoading(false);
        return;
      }

      // Get team members (only same team)
      const { data: members, error: membersError } = await supabase
        .from('profiles')
        .select('user_id, first_name, last_name, email')
        .eq('team_id', profile.team_id)
        .neq('user_id', user.id);

      if (membersError) throw membersError;

      console.log('Found team members:', members?.length || 0, members);
      setTeamMembers(members || []);

      // Get currently shared users
      const { data: sharesData, error: sharesError } = await supabase
        .from('d2d_area_shares')
        .select('id, shared_with')
        .eq('area_id', areaId);

      if (sharesError) throw sharesError;

      // Get profile information for shared users
      if (sharesData && sharesData.length > 0) {
        const userIds = sharesData.map(s => s.shared_with);
        const { data: profilesData, error: profilesError } = await supabase
          .from('profiles')
          .select('user_id, first_name, last_name, email')
          .in('user_id', userIds);

        if (profilesError) throw profilesError;

        const sharesWithProfiles = sharesData.map(share => {
          const profile = profilesData?.find(p => p.user_id === share.shared_with);
          return {
            id: share.id,
            shared_with: share.shared_with,
            profiles: {
              first_name: profile?.first_name || '',
              last_name: profile?.last_name || '',
              email: profile?.email || ''
            }
          };
        });

        setSharedUsers(sharesWithProfiles);
      } else {
        setSharedUsers([]);
      }
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Fehler beim Laden der Daten');
    } finally {
      setLoading(false);
    }
  };

  const handleShare = async () => {
    if (!selectedUserId || !user || isSharing) return;

    setIsSharing(true);
    try {
      const { error } = await supabase
        .from('d2d_area_shares')
        .insert({
          area_id: areaId,
          shared_by: user.id,
          shared_with: selectedUserId
        });

      if (error) throw error;

      toast.success('Gebiet erfolgreich geteilt');
      setSelectedUserId('');
      loadData();
      onSharesUpdated?.(); // Notify parent to update share counts
    } catch (error: any) {
      console.error('Error sharing area:', error);
      if (error.code === '23505') {
        toast.error('Dieses Gebiet wurde bereits mit diesem Benutzer geteilt');
      } else {
        toast.error('Fehler beim Teilen des Gebiets');
      }
    } finally {
      setIsSharing(false);
    }
  };

  const handleUnshare = async (shareId: string) => {
    if (!user) return;

    try {
      const { error } = await supabase
        .from('d2d_area_shares')
        .delete()
        .eq('id', shareId);

      if (error) throw error;

      toast.success('Freigabe entfernt');
      loadData();
      onSharesUpdated?.(); // Notify parent to update share counts
    } catch (error) {
      console.error('Error removing share:', error);
      toast.error('Fehler beim Entfernen der Freigabe');
    }
  };

  const availableMembers = teamMembers.filter(
    member => !sharedUsers.some(share => share.shared_with === member.user_id)
  );

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{areaName}</DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin" />
          </div>
        ) : !isOwner ? (
          // User received this shared area - show owner and who shared it
          <div className="space-y-3">
            <div className="p-4 bg-muted rounded-lg">
              <h3 className="text-sm font-medium mb-3">Gebietsinformationen</h3>
              
              {areaOwner && (
                <div className="mb-4">
                  <p className="text-xs text-muted-foreground mb-1">Eigentümer</p>
                  <p className="text-sm font-medium">
                    {areaOwner.first_name} {areaOwner.last_name}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {lightMaskEmail(areaOwner.email)}
                  </p>
                </div>
              )}

              {sharedByUser && (
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Freigegeben von</p>
                  <p className="text-sm font-medium">
                    {sharedByUser.first_name} {sharedByUser.last_name}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {lightMaskEmail(sharedByUser.email)}
                  </p>
                </div>
              )}

              {!areaOwner && !sharedByUser && (
                <p className="text-sm text-muted-foreground">
                  Informationen nicht verfügbar
                </p>
              )}
            </div>
          </div>
        ) : (
          // Owner view - full sharing functionality
          <div className="space-y-6">
            {/* Add new share */}
            <div className="space-y-3">
              <h3 className="text-sm font-medium">Mit Teammitglied teilen</h3>
              {availableMembers.length > 0 ? (
                <div className="flex gap-2">
                  <Select value={selectedUserId} onValueChange={setSelectedUserId}>
                    <SelectTrigger className="flex-1">
                      <SelectValue placeholder="Teammitglied auswählen..." />
                    </SelectTrigger>
                    <SelectContent>
                      {availableMembers.map(member => (
                        <SelectItem key={member.user_id} value={member.user_id}>
                          {member.first_name} {member.last_name} ({maskEmail(member.email)})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button
                    onClick={handleShare}
                    disabled={!selectedUserId || isSharing}
                    size="sm"
                  >
                    {isSharing ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <UserPlus className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              ) : (
                <div className="text-sm text-muted-foreground space-y-2">
                  <p>
                    {teamMembers.length === 0 
                      ? 'Keine anderen Teammitglieder in Ihrem Team gefunden.'
                      : 'Alle Teammitglieder haben bereits Zugriff auf dieses Gebiet.'}
                  </p>
                </div>
              )}
            </div>

            {/* Currently shared with */}
            <div className="space-y-3">
              <h3 className="text-sm font-medium">Geteilt mit</h3>
              {sharedUsers.length > 0 ? (
                <div className="space-y-2">
                  {sharedUsers.map(share => (
                    <div
                      key={share.id}
                      className="flex items-center justify-between p-3 border rounded-lg"
                    >
                      <div className="flex-1">
                        <p className="text-sm font-medium">
                          {share.profiles.first_name} {share.profiles.last_name}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {share.profiles.email}
                        </p>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleUnshare(share.id)}
                        className="h-8 w-8 p-0"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">
                  Noch nicht mit Teammitgliedern geteilt
                </p>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};